// frontend/src/pages/KanbanTablero.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import kanbanService from '../services/kanbanService';
import { clienteService, proyectoService } from '../services/onebaseService';
import contentService from '../services/contentService';
import usuariosService from '../services/usuariosService';
import Modal from '../components/Modal';
import PostDetailModal from '../components/PostDetailModal';
import Loading from '../components/Loading';
import { toast } from 'react-toastify';
import { DndContext, DragOverlay, closestCorners, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCronometro } from '../context/CronometroContext';
import './KanbanTablero.css';

// Función para obtener iniciales de un nombre
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Función para generar un color basado en el nombre
const getAvatarColor = (name) => {
  if (!name) return '#9ca3af';

  const colors = [
    '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#69B32D',
    '#69B32D', '#ec4899', '#14b8a6', '#f97316', '#06b6d4'
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const KanbanTablero = ({ boardId: propBoardId = null, onClose = null } = {}) => {
  const { boardId: paramBoardId } = useParams();
  const navigate = useNavigate();

  // Usar boardId de props (si viene del modal) o de params (si viene de ruta)
  const boardId = propBoardId || paramBoardId;

  // Usar contexto de cronómetro global
  const { refrescarTareasActivas, registrarUpdateCallback, timers: globalTimers, iniciarCronometro } = useCronometro();

  const [board, setBoard] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [usuariosCuenta, setUsuariosCuenta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localTimers, setLocalTimers] = useState({}); // ⭐ Estado local para timers

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTimeLogsModal, setShowTimeLogsModal] = useState(false);
  const [showManualTimeForm, setShowManualTimeForm] = useState(false);
  const [showPostDetailModal, setShowPostDetailModal] = useState(false);

  const [editingTask, setEditingTask] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [timeLogs, setTimeLogs] = useState([]);
  const [manualTimeForm, setManualTimeForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    minutos: '',
    notas: ''
  });

  const [activeId, setActiveId] = useState(null);
  const [activeType, setActiveType] = useState(null); // 'task' o 'column'
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Estados para clientes, proyectos y posts
  const [clientes, setClientes] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [proyectosFiltrados, setProyectosFiltrados] = useState([]);
  const [posts, setPosts] = useState([]);
  const [postsFiltrados, setPostsFiltrados] = useState([]);

  const [taskForm, setTaskForm] = useState({
    nombre_tarea: '',
    descripcion: '',
    status_personalizado: '',
    asignado_a: '',
    cliente: '',
    proyecto: '',
    post_asignado: '',
    fecha_entrega: '',
    tags: ''
  });

  const [statusForm, setStatusForm] = useState({
    nombre: '',
    color: '#6b7280'
  });

  const [inviteForm, setInviteForm] = useState({
    invitado_email: '',
    rol: 'viewer'
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
        delay: 100,
      },
    })
  );

  // ⭐ Sincronizar globalTimers con localTimers para forzar re-render
  // Solo actualizar timers que están activos en globalTimers
  useEffect(() => {
    setLocalTimers(prev => {
      const updated = { ...prev };
      // Actualizar solo los timers que existen en globalTimers (tareas activas)
      Object.keys(globalTimers).forEach(key => {
        updated[key] = globalTimers[key];
      });
      return updated;
    });
  }, [globalTimers]);

  // Suscribirse a cambios del contexto global
  useEffect(() => {
    const unsubscribe = registrarUpdateCallback((tipo, tareaId, data) => {
      if (tipo === 'detener') {
        // Actualizar la tarea con el nuevo tiempo total
        setTasks(prevTasks =>
          prevTasks.map(task =>
            task.id === tareaId
              ? {
                  ...task,
                  is_timer_active: false,
                  timer_started_at: null,
                  total_time_seconds: data.total_time_seconds
                }
              : task
          )
        );

        // ⭐ Actualizar localTimers con el tiempo final
        setLocalTimers(prev => ({
          ...prev,
          [tareaId]: data.total_time_seconds
        }));
      } else if (tipo === 'iniciar') {
        // Actualizar el estado de la tarea
        setTasks(prevTasks =>
          prevTasks.map(task =>
            task.id === tareaId
              ? {
                  ...task,
                  is_timer_active: true,
                  timer_started_at: data.timer_started_at || new Date().toISOString()
                }
              : task
          )
        );
      }
    });

    return unsubscribe;
  }, [registrarUpdateCallback]);

  // Cargar clientes y proyectos
  useEffect(() => {
    const cargarClientesYProyectos = async () => {
      try {
        const [clientesRes, proyectosRes] = await Promise.all([
          clienteService.getAll(),
          proyectoService.getAll()
        ]);
        setClientes(clientesRes.data.results || clientesRes.data || []);
        setProyectos(proyectosRes.data.results || proyectosRes.data || []);
      } catch (error) {
        console.error('Error cargando clientes y proyectos:', error);
      }
    };
    cargarClientesYProyectos();
  }, []);

  // Filtrar proyectos por cliente seleccionado
  useEffect(() => {
    if (taskForm.cliente) {
      const proyectosPorCliente = proyectos.filter(
        p => p.cliente === parseInt(taskForm.cliente)
      );
      setProyectosFiltrados(proyectosPorCliente);
    } else {
      setProyectosFiltrados([]);
    }
  }, [taskForm.cliente, proyectos]);

  // Cargar y filtrar posts por cliente y proyecto
  useEffect(() => {
    const cargarPosts = async () => {
      if (taskForm.cliente && taskForm.proyecto) {
        try {
          const response = await contentService.getPosts({
            cliente: taskForm.cliente,
            proyecto: taskForm.proyecto
          });
          const postsData = response.data.results || response.data || [];
          setPostsFiltrados(postsData);
        } catch (error) {
          console.error('Error cargando posts:', error);
          setPostsFiltrados([]);
        }
      } else {
        setPostsFiltrados([]);
      }
    };

    cargarPosts();
  }, [taskForm.cliente, taskForm.proyecto]);

  useEffect(() => {
    cargarDatos();
  }, [boardId]);

  // Cargar usuarios de la cuenta al montar el componente
  useEffect(() => {
    const cargarUsuarios = async () => {
      try {
        const response = await usuariosService.getUsuarios();
        setUsuariosCuenta(response.usuarios || response || []);
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
      }
    };
    cargarUsuarios();
  }, []);

  // 🆕 Escuchar eventos de actualización de tareas desde PostDetailModal
  useEffect(() => {
    const handleTareaActualizada = () => {
      console.log('📢 Se detectó actualización de tarea desde PostDetailModal');
      // Recargar todos los datos del kanban
      cargarDatos();
    };

    const handleTareaCreada = () => {
      console.log('📢 Se detectó creación de tarea desde PostDetailModal');
      // Recargar todos los datos del kanban
      cargarDatos();
    };

    window.addEventListener('tareaActualizada', handleTareaActualizada);
    window.addEventListener('tareaCreada', handleTareaCreada);

    // Cleanup
    return () => {
      window.removeEventListener('tareaActualizada', handleTareaActualizada);
      window.removeEventListener('tareaCreada', handleTareaCreada);
    };
  }, [boardId]);

  // ⭐ Cleanup para prevenir que listeners del DndContext queden pegados
  useEffect(() => {
    return () => {
      // Limpiar cualquier estado activo cuando el componente se desmonta
      setActiveId(null);
      setActiveType(null);
      setTasks([]);
      setBoard(null);
      setStatuses([]);
      setMembers([]);

      // Forzar que React Router maneje la navegación eliminando cualquier listener pendiente
      setTimeout(() => {
        // Dispare un evento para limpiar cualquier estado pendiente del DndContext
        const event = new Event('dragend', { bubbles: true });
        document.dispatchEvent(event);
      }, 0);
    };
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [boardData, statusesData, tasksData, membersData] = await Promise.all([
        kanbanService.getBoard(boardId),
        kanbanService.getStatuses(boardId),
        kanbanService.getTasks(boardId),
        kanbanService.getMembers(boardId)
      ]);

      setBoard(boardData);
      setStatuses(statusesData.statuses || []);
      setTasks(tasksData.tasks || []);
      setMembers(membersData.members || []);

      // Cargar invitaciones si es propietario o editor
      if (boardData.rol_usuario === 'owner' || boardData.rol_usuario === 'editor') {
        try {
          const invitationsData = await kanbanService.getBoardInvitations(boardId);
          console.log('📬 Invitaciones cargadas:', invitationsData);
          setInvitations(invitationsData.invitations || []);
        } catch (error) {
          console.error('Error al cargar invitaciones:', error);
        }
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar el tablero');
      if (onClose) {
        onClose();
      } else {
        navigate('/kanban');
      }
    } finally {
      setLoading(false);
    }
  };

  // ========== GESTIÓN DE TAREAS ==========

  // Helper para limpiar datos del formulario antes de enviar
  const prepareTaskData = (formData) => {
    const cleanData = { ...formData };

    // Convertir strings vacíos a null para campos opcionales
    const optionalFields = ['cliente', 'proyecto', 'asignado_a', 'status_personalizado', 'fecha_entrega'];
    optionalFields.forEach(field => {
      if (cleanData[field] === '') {
        cleanData[field] = null;
      } else if (cleanData[field] && ['cliente', 'proyecto', 'asignado_a', 'status_personalizado'].includes(field)) {
        // Convertir a número entero si es un ID
        cleanData[field] = parseInt(cleanData[field], 10);
      }
    });

    return cleanData;
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const taskData = prepareTaskData(taskForm);
      await kanbanService.createTask(boardId, taskData);
      toast.success('Tarea creada exitosamente');
      setShowTaskModal(false);
      resetTaskForm();
      cargarDatos();
    } catch (error) {
      console.error('Error al crear tarea:', error);
      const errorMessage = error.response?.data?.cliente?.[0] ||
                           error.response?.data?.proyecto?.[0] ||
                           'Error al crear tarea';
      toast.error(errorMessage);
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      const taskData = prepareTaskData(taskForm);
      await kanbanService.updateTask(editingTask.id, taskData);
      toast.success('Tarea actualizada exitosamente');
      setShowTaskModal(false);
      setEditingTask(null);
      resetTaskForm();
      cargarDatos();
    } catch (error) {
      console.error('Error al actualizar tarea:', error);
      const errorMessage = error.response?.data?.cliente?.[0] ||
                           error.response?.data?.proyecto?.[0] ||
                           'Error al actualizar tarea';
      toast.error(errorMessage);
    }
  };

  const handleDeleteTask = async (taskId, nombre) => {
    if (window.confirm(`¿Eliminar la tarea "${nombre}"?`)) {
      try {
        await kanbanService.deleteTask(taskId);
        toast.success('Tarea eliminada exitosamente');
        cargarDatos();
      } catch (error) {
        console.error('Error al eliminar tarea:', error);
        toast.error('Error al eliminar tarea');
      }
    }
  };

  const handleToggleTimer = async (task) => {
    try {
      if (task.is_timer_active) {
        // Detener cronómetro
        const response = await kanbanService.stopTimer(task.id);

        // ⭐ Actualizar estado local inmediatamente con el tiempo final
        setTasks(prevTasks =>
          prevTasks.map(t =>
            t.id === task.id
              ? {
                  ...t,
                  is_timer_active: false,
                  timer_started_at: null,
                  total_time_seconds: response.total_time_seconds || t.total_time_seconds
                }
              : t
          )
        );

        // ⭐ Actualizar localTimers con el tiempo final para que se muestre inmediatamente
        setLocalTimers(prev => ({
          ...prev,
          [task.id]: response.total_time_seconds || prev[task.id] || 0
        }));

        // ⭐ Refrescar el contexto global para que desaparezca el toast
        setTimeout(() => {
          refrescarTareasActivas();
        }, 100);

        toast.success('Cronómetro detenido');
      } else {
        // Iniciar cronómetro
        const response = await kanbanService.startTimer(task.id);

        // ⭐ Actualizar estado local inmediatamente con los datos de la respuesta
        setTasks(prevTasks =>
          prevTasks.map(t =>
            t.id === task.id
              ? {
                  ...t,
                  is_timer_active: true,
                  timer_started_at: response.timer_started_at || new Date().toISOString(),
                  timer_active_user_name: response.timer_active_user_name || t.timer_active_user_name
                }
              : t
          )
        );

        // ⭐ Llamar a iniciarCronometro del contexto para actualizar tareasActivas y notificar
        iniciarCronometro(task.id, {
          ...task,
          is_timer_active: true,
          timer_started_at: response.timer_started_at || new Date().toISOString(),
          timer_active_user_name: response.timer_active_user_name,
          total_time_seconds: response.total_time_seconds || task.total_time_seconds
        });

        toast.success(`Cronómetro iniciado`);
      }
    } catch (error) {
      console.error('Error al cambiar cronómetro:', error);
      toast.error(error.error || 'Error al cambiar el cronómetro');
      // Si falla, recargar para mantener consistencia
      cargarDatos();
    }
  };

  const openEditTaskModal = (task) => {
    setEditingTask(task);

    // Extraer el ID del usuario asignado correctamente
    let asignadoAId = '';
    if (task.asignado_a) {
      // Si asignado_a es un objeto con id, usar el id
      if (typeof task.asignado_a === 'object' && task.asignado_a.id) {
        asignadoAId = task.asignado_a.id;
      } else {
        // Si es solo un número, usarlo directamente
        asignadoAId = task.asignado_a;
      }
    }

    setTaskForm({
      nombre_tarea: task.nombre_tarea,
      descripcion: task.descripcion || '',
      status_personalizado: task.status_personalizado?.id || task.status?.id,
      asignado_a: asignadoAId,
      cliente: task.cliente || '',
      proyecto: task.proyecto || '',
      post_asignado: task.post_asignado || '',
      fecha_entrega: task.fecha_entrega || '',
      tags: task.tags || ''
    });
    setShowTaskModal(true);
  };

  const resetTaskForm = () => {
    setTaskForm({
      nombre_tarea: '',
      descripcion: '',
      status_personalizado: statuses[0]?.id || '',
      asignado_a: '',
      cliente: '',
      proyecto: '',
      post_asignado: '',
      fecha_entrega: '',
      tags: ''
    });
  };

  const handleViewTimeLogs = async (task) => {
    try {
      const logsData = await kanbanService.getTimeLogs({ tarea: task.id });
      setTimeLogs(logsData.results || logsData || []);
      setSelectedTask(task);
      setShowTimeLogsModal(true);
      setShowManualTimeForm(false);
      setManualTimeForm({
        fecha: new Date().toISOString().split('T')[0],
        minutos: '',
        notas: ''
      });
    } catch (error) {
      console.error('Error al cargar logs de tiempo:', error);
      toast.error('Error al cargar historial de tiempo');
    }
  };

  const handleAddManualTime = async (e) => {
    e.preventDefault();

    if (!manualTimeForm.minutos || manualTimeForm.minutos <= 0) {
      toast.error('Por favor ingresa un tiempo válido en minutos');
      return;
    }

    try {
      await kanbanService.createManualTimeLog({
        tarea: selectedTask.id,
        fecha: manualTimeForm.fecha,
        minutos: parseInt(manualTimeForm.minutos),
        notas: manualTimeForm.notas
      });

      toast.success('Tiempo registrado exitosamente');

      // Recargar los time logs
      const logsData = await kanbanService.getTimeLogs({ tarea: selectedTask.id });
      setTimeLogs(logsData.results || logsData || []);

      // Limpiar formulario
      setManualTimeForm({
        fecha: new Date().toISOString().split('T')[0],
        minutos: '',
        notas: ''
      });
      setShowManualTimeForm(false);

      // Recargar datos del tablero para actualizar el tiempo total
      cargarDatos();
    } catch (error) {
      console.error('Error al registrar tiempo manual:', error);
      toast.error(error.response?.data?.error || 'Error al registrar tiempo');
    }
  };

  const handleOpenPostDetail = (postId, e) => {
    if (e) {
      e.stopPropagation(); // Evitar que se propague el click
    }
    setSelectedPostId(postId);
    setShowPostDetailModal(true);
  };

  const handleClosePostDetail = () => {
    setShowPostDetailModal(false);
    setSelectedPostId(null);
  };

  // ========== GESTIÓN DE ESTADOS ==========

  const handleCreateStatus = async (e) => {
    e.preventDefault();
    try {
      await kanbanService.createStatus(boardId, statusForm);
      toast.success('Estado creado exitosamente');
      setShowStatusModal(false);
      setStatusForm({ nombre: '', color: '#6b7280' });
      cargarDatos();
    } catch (error) {
      console.error('Error al crear estado:', error);
      toast.error('Error al crear estado');
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    try {
      await kanbanService.updateStatus(boardId, editingStatus.id, statusForm);
      toast.success('Estado actualizado exitosamente');
      setShowStatusModal(false);
      setEditingStatus(null);
      setStatusForm({ nombre: '', color: '#6b7280' });
      cargarDatos();
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      toast.error('Error al actualizar estado');
    }
  };

  const handleDeleteStatus = async (statusId, nombre) => {
    if (window.confirm(`¿Estás seguro de eliminar el estado "${nombre}"?`)) {
      try {
        await kanbanService.deleteStatus(boardId, statusId);
        toast.success('Estado eliminado exitosamente');
        cargarDatos();
      } catch (error) {
        console.error('Error al eliminar estado:', error);
        const errorMsg = error.response?.data?.error || 'Error al eliminar estado';
        toast.error(errorMsg);
      }
    }
  };

  const openEditStatusModal = (status) => {
    setEditingStatus(status);
    setStatusForm({
      nombre: status.nombre,
      color: status.color
    });
    setShowStatusModal(true);
  };

  // ========== GESTIÓN DE MIEMBROS E INVITACIONES ==========

  const handleSendInvitation = async (e) => {
    e.preventDefault();
    try {
      console.log('📤 Enviando invitación:', { boardId, inviteForm });
      const response = await kanbanService.sendInvitation(boardId, inviteForm);
      console.log('✅ Invitación enviada exitosamente:', response);
      toast.success('Invitación enviada exitosamente');
      setShowInviteModal(false);
      setInviteForm({ invitado_email: '', rol: 'viewer' });
      await cargarDatos(); // Esperar a que recargue los datos
    } catch (error) {
      console.error('❌ Error al enviar invitación:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Error al enviar invitación');
    }
  };

  const handleUpdateMemberRole = async (memberId, newRole) => {
    try {
      await kanbanService.updateMemberRole(boardId, memberId, { role: newRole });
      toast.success('Rol actualizado exitosamente');
      cargarDatos();
    } catch (error) {
      console.error('Error al actualizar rol:', error);
      toast.error('Error al actualizar rol');
    }
  };

  const handleRemoveMember = async (memberId, nombre) => {
    if (window.confirm(`¿Remover a ${nombre} del tablero?`)) {
      try {
        await kanbanService.removeMember(boardId, memberId);
        toast.success('Miembro removido exitosamente');
        cargarDatos();
      } catch (error) {
        console.error('Error al remover miembro:', error);
        toast.error('Error al remover miembro');
      }
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (window.confirm('¿Cancelar esta invitación?')) {
      try {
        await kanbanService.cancelInvitation(invitationId);
        toast.success('Invitación cancelada exitosamente');
        cargarDatos();
      } catch (error) {
        console.error('Error al cancelar invitación:', error);
        toast.error('Error al cancelar invitación');
      }
    }
  };

  // ========== DRAG & DROP ==========

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);

    // Determinar si es una columna o una tarea
    const isColumn = statuses.some(s => s.id === active.id);
    setActiveType(isColumn ? 'column' : 'task');
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      setActiveType(null);
      return;
    }

    // Si estamos arrastrando una columna
    if (activeType === 'column') {
      const oldIndex = statuses.findIndex(s => s.id === active.id);
      let newIndex = statuses.findIndex(s => s.id === over.id);

      // Si no encuentra exactamente la columna, buscar el padre (columna contenedora)
      if (newIndex === -1) {
        // Buscar en toda la estructura el contenedor de la columna
        const overContainer = active.id === over.id ? oldIndex : statuses.findIndex(s => s.id === over.id);
        newIndex = overContainer !== -1 ? overContainer : oldIndex;
      }

      if (oldIndex !== newIndex && oldIndex !== -1 && newIndex !== -1) {
        // Reordenar columnas optimisticamente
        const newStatuses = arrayMove(statuses, oldIndex, newIndex);
        setStatuses(newStatuses);

        // Enviar al backend
        try {
          // El backend espera { orden: [id1, id2, id3, ...] }
          const statusesOrder = newStatuses.map(status => status.id);

          await kanbanService.reorderStatuses(boardId, { orden: statusesOrder });
          toast.success('Estados reordenados exitosamente');
        } catch (error) {
          console.error('Error al reordenar estados:', error);
          toast.error('Error al reordenar estados');
          cargarDatos(); // Recargar en caso de error
        }
      }
    }
    // Si estamos arrastrando una tarea
    else if (activeType === 'task') {
      const taskId = active.id;
      let newStatusId = over.id;

      // Si over.id es una tarea (no una columna), encontrar su columna
      const overTask = tasks.find(t => t.id === newStatusId);
      if (overTask && overTask.status?.id) {
        newStatusId = overTask.status.id;
      } else if (!statuses.find(s => s.id === newStatusId)) {
        // Si no es una columna ni una tarea válida, no hacer nada
        console.warn('No se pudo determinar la columna destino');
        setActiveId(null);
        setActiveType(null);
        return;
      }

      const taskBeingMoved = tasks.find(t => t.id === taskId);
      const currentStatusId = taskBeingMoved?.status?.id;

      // Solo mover si la columna destino es diferente
      if (taskId && newStatusId && currentStatusId !== newStatusId) {
        // Actualizar UI optimisticamente
        setTasks(prevTasks =>
          prevTasks.map(task =>
            task.id === taskId
              ? { ...task, status: statuses.find(s => s.id === newStatusId) }
              : task
          )
        );

        try {
          await kanbanService.moveTask(taskId, newStatusId);
          toast.success('Tarea movida exitosamente');
        } catch (error) {
          console.error('Error al mover tarea:', error);
          toast.error('Error al mover tarea');
          cargarDatos(); // Recargar en caso de error
        }
      }
    }

    setActiveId(null);
    setActiveType(null);
  };

  // ========== FILTROS Y BÚSQUEDA ==========

  const tareasFiltradas = useMemo(() => {
    return tasks.filter(task => {
      const matchUsuario = !filtroUsuario || task.asignado_a?.id === parseInt(filtroUsuario);
      const matchEstado = !filtroEstado || task.status?.id === parseInt(filtroEstado);
      const matchSearch = !searchTerm ||
        task.nombre_tarea.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchUsuario && matchEstado && matchSearch;
    });
  }, [tasks, filtroUsuario, filtroEstado, searchTerm]);

  const getPrioridadColor = (prioridad) => {
    const colores = {
      'alta': '#ef4444',
      'media': '#f59e0b',
      'baja': '#10b981'
    };
    return colores[prioridad] || '#6b7280';
  };

  const getRolLabel = (rol) => {
    const roles = {
      'owner': 'Propietario',
      'editor': 'Editor',
      'viewer': 'Visualizador'
    };
    return roles[rol] || rol;
  };

  const formatTime = (seconds) => {
    const totalSeconds = seconds || 0;
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <Loading />;
  }

  if (!board) {
    return (
      <div className="error-container">
        <span style={{ fontSize: '3rem' }}>⚠️</span>
        <h2>Tablero no encontrado</h2>
        {onClose ? (
          <button onClick={onClose} className="btn-primary">Volver a tableros</button>
        ) : (
          <Link to="/kanban" className="btn-primary">Volver a tableros</Link>
        )}
      </div>
    );
  }

  const puedeEditar = board.rol_usuario === 'owner' || board.rol_usuario === 'editor';
  const esPropietario = board.rol_usuario === 'owner';

  return (
    <div className="kanban-tablero-container">
      {/* Header */}
      <div className="tablero-header">
        <div className="tablero-header-left">
          {onClose ? (
            <button onClick={onClose} className="btn-back">
              ⬅️
            </button>
          ) : (
            <Link to="/kanban" className="btn-back">
              ⬅️
            </Link>
          )}
          <div>
            <h1>
              <span className="tablero-icon">{board.icono}</span>
              {board.nombre}
            </h1>
            <p className="text-muted">{board.descripcion || 'Sin descripción'}</p>
          </div>
        </div>

        <div className="tablero-header-right">
          <button
            className="btn-icon-round estado-btn"
            onClick={() => setShowMembersPanel(!showMembersPanel)}
            title="Ver miembros"
          >
            👥
            <span className="badge-count">{members.length}</span>
          </button>

          {puedeEditar && (
            <>
              <button
                className="btn-primary"
                onClick={() => {
                  setEditingStatus(null);
                  setStatusForm({ nombre: '', color: '#6b7280' });
                  setShowStatusModal(true);
                }}
              >
                📊 Nuevo Estado
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  setEditingTask(null);
                  resetTaskForm();
                  setShowTaskModal(true);
                }}
              >
                ➕ Nueva Tarea
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="tablero-filtros">
        <div className="search-bar">
          <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '1rem' }}>🔍</span>
          <input
            type="text"
            placeholder="Buscar tareas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={filtroUsuario}
          onChange={(e) => setFiltroUsuario(e.target.value)}
          className="form-control"
        >
          <option value="">Todos los usuarios</option>
          {members.map(member => (
            <option key={member.id} value={member.usuario_id}>
              {member.usuario_nombre}
            </option>
          ))}
        </select>

        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          className="form-control"
        >
          <option value="">Todos los estados</option>
          {statuses.map(status => (
            <option key={status.id} value={status.id}>
              {status.nombre}
            </option>
          ))}
        </select>

        {(filtroUsuario || filtroEstado || searchTerm) && (
          <button
            className="estado-btn"
            onClick={() => {
              setFiltroUsuario('');
              setFiltroEstado('');
              setSearchTerm('');
            }}
          >
            ❌ Limpiar filtros
          </button>
        )}
      </div>

      {/* Tablero Kanban con DndContext */}
      <div style={{ width: '100%', position: 'relative' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          autoFocus={false}
        >
          <div className="kanban-board">
          {statuses.length === 0 ? (
            <div className="empty-state">
              <span style={{ fontSize: '3rem' }}>📊</span>
              <h3>No hay estados</h3>
              <p>Crea el primer estado para organizar tus tareas</p>
              {puedeEditar && (
                <button
                  className="btn-primary"
                  onClick={() => {
                    setEditingStatus(null);
                    setStatusForm({ nombre: '', color: '#6b7280' });
                    setShowStatusModal(true);
                  }}
                >
                  ➕ Crear Estado
                </button>
              )}
            </div>
          ) : (
            <SortableContext items={statuses.map(s => s.id)} strategy={horizontalListSortingStrategy}>
              {statuses.map(status => (
                <KanbanColumn
                  key={status.id}
                  status={status}
                  tasks={tareasFiltradas.filter(task => task.status?.id === status.id)}
                  onEditTask={openEditTaskModal}
                  onDeleteTask={handleDeleteTask}
                  onToggleTimer={handleToggleTimer}
                  onViewTimeLogs={handleViewTimeLogs}
                  onOpenPostDetail={handleOpenPostDetail}
                  onEditStatus={openEditStatusModal}
                  onDeleteStatus={handleDeleteStatus}
                  getPrioridadColor={getPrioridadColor}
                  formatTime={formatTime}
                  localTimers={localTimers}
                  puedeEditar={puedeEditar}
                  esPropietario={esPropietario}
                />
              ))}
            </SortableContext>
          )}
        </div>

      <DragOverlay>
        {activeId && activeType === 'task' ? (
          <div className="task-card dragging">
            <div className="task-card-header">
              <h4>{tasks.find(t => t.id === activeId)?.nombre_tarea}</h4>
              <span style={{ fontSize: '1.2rem', opacity: 0.8 }}>👆</span>
            </div>
            {tasks.find(t => t.id === activeId)?.descripcion && (
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#6b7280', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {tasks.find(t => t.id === activeId)?.descripcion}
              </p>
            )}
          </div>
        ) : activeId && activeType === 'column' ? (
          <div className="kanban-column dragging-column-overlay">
            <div className="kanban-column-header" style={{ borderTopColor: statuses.find(s => s.id === activeId)?.color }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <span className="status-indicator" style={{ backgroundColor: statuses.find(s => s.id === activeId)?.color }}></span>
                {statuses.find(s => s.id === activeId)?.nombre}
                <span style={{ fontSize: '1rem', opacity: 0.8 }}>👆</span>
              </h3>
            </div>
            <div style={{ padding: '0 1rem 1rem 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
              📊 {tasks.filter(t => t.status?.id === activeId).length} tareas
            </div>
          </div>
        ) : null}
      </DragOverlay>
        </DndContext>
      </div>

      {/* Panel de Miembros */}
      {showMembersPanel && (
        <div className="members-panel" style={{ pointerEvents: 'auto' }}>
          <div className="members-panel-header">
            <h3>
              👥 Miembros del tablero
            </h3>
            <button
              className="btn-close"
              onClick={() => setShowMembersPanel(false)}
            >
              ❌
            </button>
          </div>

          <div className="members-panel-body">
            {(esPropietario || board?.rol_usuario === 'editor') && (
              <button
                className="btn-primary btn-block"
                onClick={() => setShowInviteModal(true)}
              >
                👤 Invitar miembro
              </button>
            )}

            <div className="members-list">
              <h4>Miembros activos ({members.length})</h4>
              {members.map(member => (
                <div key={member.id} className="member-item">
                  <div className="member-info">
                    <div className="member-avatar">
                      {member.usuario_nombre.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="member-name">{member.usuario_nombre}</p>
                      <p className="member-email">{member.usuario_email}</p>
                    </div>
                  </div>

                  <div className="member-actions">
                    {esPropietario && member.rol !== 'owner' ? (
                      <>
                        <select
                          value={member.rol}
                          onChange={(e) => handleUpdateMemberRole(member.id, e.target.value)}
                          className="form-control form-control-sm"
                        >
                          <option value="viewer">Visualizador</option>
                          <option value="editor">Editor</option>
                          <option value="owner">Propietario</option>
                        </select>
                        <button
                          className="btn-icon-small text-danger"
                          onClick={() => handleRemoveMember(member.id, member.usuario_nombre)}
                          title="Remover miembro"
                        >
                          🗑️
                        </button>
                      </>
                    ) : (
                      <span className="badge badge-primary">{getRolLabel(member.rol)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {(esPropietario || board?.rol_usuario === 'editor') && invitations.length > 0 && (
              <div className="invitations-list">
                <h4>Invitaciones pendientes ({invitations.length})</h4>
                {invitations.map(invitation => (
                  <div key={invitation.id} className="invitation-item">
                    <div>
                      <p className="invitation-email">{invitation.invitado_email}</p>
                      <p className="invitation-info">
                        Rol: {getRolLabel(invitation.rol)} •
                        Expira: {new Date(invitation.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      className="btn-icon-small text-danger"
                      onClick={() => handleCancelInvitation(invitation.id)}
                      title="Cancelar invitación"
                    >
                      ❌
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Crear/Editar Tarea */}
      <Modal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        title={editingTask ? 'Editar Tarea' : 'Nueva Tarea'}
        subtitle={editingTask ? 'Actualiza los detalles de la tarea' : 'Crea una nueva tarea en el tablero'}
        icon={editingTask ? '✏️' : '➕'}
        size="l"
        footer={
          <div style={{ display: 'flex', gap: '1rem', width: '100%', justifyContent: 'flex-end' }}>
            <button type="button" className="estado-btn" onClick={() => setShowTaskModal(false)}>
              Cancelar
            </button>
            <button type="submit" form="task-form" className="btn-primary">
              {editingTask ? 'Guardar Cambios' : 'Crear Tarea'}
            </button>
          </div>
        }
      >
        <form id="task-form" onSubmit={editingTask ? handleUpdateTask : handleCreateTask}>
          <div className="form-group">
            <label className="form-label required">Título</label>
            <input
              type="text"
              className="form-control"
              value={taskForm.nombre_tarea}
              onChange={(e) => setTaskForm({ ...taskForm, nombre_tarea: e.target.value })}
              required
              placeholder="Nombre de la tarea"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Descripción</label>
            <textarea
              className="form-control form-control-lg"
              rows="4"
              value={taskForm.descripcion}
              onChange={(e) => setTaskForm({ ...taskForm, descripcion: e.target.value })}
              placeholder="Descripción de la tarea (opcional)"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label required">Estado</label>
              <select
                className="form-control"
                value={taskForm.status_personalizado}
                onChange={(e) => setTaskForm({ ...taskForm, status_personalizado: e.target.value })}
                required
              >
                <option value="">Seleccionar estado</option>
                {statuses.map(status => (
                  <option key={status.id} value={status.id}>
                    {status.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Asignado a</label>
              <select
                className="form-control"
                value={taskForm.asignado_a}
                onChange={(e) => setTaskForm({ ...taskForm, asignado_a: e.target.value })}
              >
                <option value="">Sin asignar</option>
                {members.map(member => (
                  <option key={member.id} value={member.usuario_id}>
                    {member.usuario_nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Cliente</label>
              <select
                className="form-control"
                value={taskForm.cliente}
                onChange={(e) => setTaskForm({ ...taskForm, cliente: e.target.value, proyecto: '' })}
              >
                <option value="">Seleccionar cliente</option>
                {clientes.map(cliente => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Proyecto</label>
              <select
                className="form-control"
                value={taskForm.proyecto}
                onChange={(e) => setTaskForm({ ...taskForm, proyecto: e.target.value, post_asignado: '' })}
                disabled={!taskForm.cliente}
              >
                <option value="">
                  {taskForm.cliente ? 'Seleccionar proyecto' : 'Primero selecciona un cliente'}
                </option>
                {proyectosFiltrados.map(proyecto => (
                  <option key={proyecto.id} value={proyecto.id}>
                    {proyecto.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Selector de Post Asignado */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Post Asignado</label>
              <select
                className="form-control"
                value={taskForm.post_asignado}
                onChange={(e) => setTaskForm({ ...taskForm, post_asignado: e.target.value })}
                disabled={!taskForm.proyecto}
              >
                <option value="">
                  {taskForm.proyecto ? 'Seleccionar post (opcional)' : 'Primero selecciona un proyecto'}
                </option>
                {postsFiltrados.map(post => (
                  <option key={post.id} value={post.id}>
                    {post.title} - {post.platform}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Fecha de entrega</label>
              <input
                type="date"
                className="form-control"
                value={taskForm.fecha_entrega}
                onChange={(e) => setTaskForm({ ...taskForm, fecha_entrega: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tags</label>
              <input
                type="text"
                className="form-control"
                value={taskForm.tags}
                onChange={(e) => setTaskForm({ ...taskForm, tags: e.target.value })}
                placeholder="etiqueta1, etiqueta2"
              />
            </div>
          </div>
        </form>
      </Modal>

        {/* Modal Crear/Editar Estado */}
        <Modal
          isOpen={showStatusModal}
          onClose={() => setShowStatusModal(false)}
          title={editingStatus ? 'Editar Estado' : 'Nuevo Estado'}
          subtitle={editingStatus ? 'Actualiza el nombre y color del estado' : 'Crea un nuevo estado para tu tablero'}
          icon={editingStatus ? '✏️' : '➕'}
          size="m"
          footer={
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button type="button" className="estado-btn" onClick={() => setShowStatusModal(false)}>
                Cancelar
              </button>
              <button type="submit" form="status-form" className="btn-primary">
                {editingStatus ? 'Guardar Cambios' : 'Crear Estado'}
              </button>
            </div>
          }
        >
          <form id="status-form" onSubmit={editingStatus ? handleUpdateStatus : handleCreateStatus}>
            <div className="form-group">
              <label className="form-label required">Nombre del estado</label>
              <input
                type="text"
                className="form-control"
                value={statusForm.nombre}
                onChange={(e) => setStatusForm({ ...statusForm, nombre: e.target.value })}
                required
                placeholder="Ej: En Progreso, Revisión, Completado"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Color</label>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <input
                  type="color"
                  className="form-control"
                  style={{ width: '80px', height: '48px', padding: '4px', cursor: 'pointer' }}
                  value={statusForm.color}
                  onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
                />
                <span
                  style={{
                    display: 'inline-block',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: statusForm.color,
                    border: '2px solid #e5e7eb'
                  }}
                  title={statusForm.color}
                />
                <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>{statusForm.color}</span>
              </div>
              <p className="form-helper-text">El color será usado en el indicador del estado</p>
            </div>
          </form>
        </Modal>

        {/* Modal Invitar Miembro */}
        <Modal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          title="Invitar Miembro"
          subtitle="Añade un nuevo miembro al tablero con el rol que desees"
          icon="👤"
          size="m"
          footer={
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button type="button" className="estado-btn" onClick={() => setShowInviteModal(false)}>
                Cancelar
              </button>
              <button type="submit" form="invite-form" className="btn-primary">
                Enviar Invitación
              </button>
            </div>
          }
        >
          <form id="invite-form" onSubmit={handleSendInvitation}>
            <div className="form-group">
              <label className="form-label required">Usuario</label>
              <select
                className="form-control"
                value={inviteForm.invitado_email}
                onChange={(e) => setInviteForm({ ...inviteForm, invitado_email: e.target.value })}
                required
              >
                <option value="">Selecciona un usuario...</option>
                {usuariosCuenta.map(usuario => (
                  <option key={usuario.id} value={usuario.email}>
                    {usuario.nombre_completo || usuario.username} ({usuario.email})
                  </option>
                ))}
              </select>
              <p className="form-helper-text">
                Selecciona un usuario de tu cuenta para invitarlo al tablero.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Rol</label>
              <select
                className="form-control"
                value={inviteForm.rol}
                onChange={(e) => setInviteForm({ ...inviteForm, rol: e.target.value })}
              >
                <option value="viewer">👁️ Visualizador (solo lectura)</option>
                <option value="editor">✏️ Editor (puede crear y editar)</option>
                <option value="owner">👑 Propietario (control total)</option>
              </select>
              <p className="form-helper-text">
                {inviteForm.rol === 'viewer' && 'El usuario podrá ver el tablero pero no podrá hacer cambios.'}
                {inviteForm.rol === 'editor' && 'El usuario podrá crear, editar y eliminar tareas.'}
                {inviteForm.rol === 'owner' && 'El usuario tendrá control total del tablero.'}
              </p>
            </div>
          </form>
        </Modal>

        {/* Modal Time Logs */}
        <Modal
          isOpen={showTimeLogsModal}
          onClose={() => {
            setShowTimeLogsModal(false);
            setShowManualTimeForm(false);
          }}
          title={`Historial de Tiempo`}
          subtitle={selectedTask?.nombre_tarea}
          icon="⏱️"
          size="l"
          footer={
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', width: '100%' }}>
              <button
                className="btn-primary"
                onClick={() => setShowManualTimeForm(!showManualTimeForm)}
                style={{
                  background: showManualTimeForm ? '#6b7280' : 'linear-gradient(135deg, #102826, #69B32D)'
                }}
              >
                {showManualTimeForm ? '❌ Cancelar' : '➕ Agregar Tiempo Manual'}
              </button>
              <button className="estado-btn" onClick={() => {
                setShowTimeLogsModal(false);
                setShowManualTimeForm(false);
              }}>
                Cerrar
              </button>
            </div>
          }
        >
          {/* Formulario de entrada manual de tiempo */}
          {showManualTimeForm && (
            <form onSubmit={handleAddManualTime} style={{
              background: '#f0f9ff',
              border: '2px solid #3b82f6',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{
                margin: '0 0 1rem 0',
                color: '#1e40af',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <i className="fas fa-clock"></i> Agregar Tiempo Manual
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#374151',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}>
                    Fecha
                  </label>
                  <input
                    type="date"
                    value={manualTimeForm.fecha}
                    onChange={(e) => setManualTimeForm({ ...manualTimeForm, fecha: e.target.value })}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#374151',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}>
                    Tiempo (minutos)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={manualTimeForm.minutos}
                    onChange={(e) => setManualTimeForm({ ...manualTimeForm, minutos: e.target.value })}
                    required
                    placeholder="ej: 30"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}>
                  Notas (opcional)
                </label>
                <textarea
                  value={manualTimeForm.notas}
                  onChange={(e) => setManualTimeForm({ ...manualTimeForm, notas: e.target.value })}
                  placeholder="Describe en qué trabajaste..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <button
                type="submit"
                className="btn-primary"
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  padding: '0.75rem'
                }}
              >
                💾 Guardar Registro de Tiempo
              </button>
            </form>
          )}

          {timeLogs.length === 0 && !showManualTimeForm ? (
            <div className="modal-empty-state">
              <div className="modal-empty-state-icon">📋</div>
              <div className="modal-empty-state-title">Sin registros de tiempo</div>
              <div className="modal-empty-state-text">
                No hay registros de tiempo para esta tarea. Inicia el cronómetro o agrega tiempo manualmente.
              </div>
            </div>
          ) : timeLogs.length > 0 ? (
            <div className="time-logs-list">
              <div style={{
                background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                color: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '0.5rem' }}>Tiempo total registrado</div>
                <div style={{ fontSize: '2rem', fontWeight: '700', fontFamily: 'monospace' }}>
                  {formatTime(timeLogs.reduce((sum, log) => sum + (log.duration_seconds || 0), 0))}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {timeLogs.map(log => (
                  <div key={log.id} style={{
                    background: '#f9fafb',
                    padding: '1rem',
                    borderRadius: '8px',
                    borderLeft: '4px solid #69B32D'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                      <div>
                        <div style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                          <i className="fas fa-user" style={{ marginRight: '0.5rem' }}></i>
                          {log.user_name || 'Usuario'}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                          <i className="fas fa-calendar" style={{ marginRight: '0.5rem' }}></i>
                          {new Date(log.started_at).toLocaleDateString('es-ES')} - {new Date(log.started_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        {log.notes && (
                          <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                            <i className="fas fa-comment" style={{ marginRight: '0.5rem' }}></i>
                            {log.notes}
                          </div>
                        )}
                      </div>
                      {log.ended_at ? (
                        <span style={{ color: '#1f2937', fontWeight: '600', fontFamily: 'monospace', fontSize: '1.1rem' }}>
                          {formatTime(log.duration_seconds)}
                        </span>
                      ) : (
                        <span style={{
                          background: '#10b981',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}>
                          En curso
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </Modal>

        {/* Modal de Detalle de Post */}
        {showPostDetailModal && selectedPostId && (
          <PostDetailModal
            postId={selectedPostId}
            onClose={handleClosePostDetail}
            onUpdate={cargarDatos}
          />
        )}
    </div>
  );
};

// Componente KanbanColumn
const KanbanColumn = ({
  status,
  tasks,
  onEditTask,
  onDeleteTask,
  onToggleTimer,
  onViewTimeLogs,
  onOpenPostDetail,
  onEditStatus,
  onDeleteStatus,
  getPrioridadColor,
  formatTime,
  localTimers,
  puedeEditar,
  esPropietario
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: status.id,
    disabled: !esPropietario // Solo el propietario puede reordenar columnas
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      className={`kanban-column ${isDragging ? 'dragging-column' : ''}`}
      ref={setNodeRef}
      style={style}
    >
      <div
        className="kanban-column-header"
        style={{ borderTopColor: status.color }}
        {...attributes}
        {...listeners}
      >
        <h3>
          {esPropietario && (
            <span className="column-drag-handle" style={{ marginRight: '0.5rem' }}>☰</span>
          )}
          <span className="status-indicator" style={{ backgroundColor: status.color }}></span>
          {status.nombre}
          <span className="task-count">{tasks.length}</span>
        </h3>
        {esPropietario && (
          <div className="dropdown">
            <button
              className="btn-icon-small"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="dropdown-menu show">
                <button onClick={() => { onEditStatus(status); setShowMenu(false); }}>
                  ✏️ Editar
                </button>
                <button
                  className="text-danger"
                  onClick={() => { onDeleteStatus(status.id, status.nombre); setShowMenu(false); }}
                  disabled={tasks.length > 0}
                  title={tasks.length > 0 ? 'No puedes eliminar un estado con tareas' : ''}
                >
                  🗑️ Eliminar {tasks.length > 0 && `(${tasks.length} tareas)`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
        <div className="kanban-column-content">
          {tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onEdit={onEditTask}
              onDelete={onDeleteTask}
              onToggleTimer={onToggleTimer}
              onViewTimeLogs={onViewTimeLogs}
              onOpenPostDetail={onOpenPostDetail}
              getPrioridadColor={getPrioridadColor}
              formatTime={formatTime}
              localTimers={localTimers}
              puedeEditar={puedeEditar}
            />
          ))}
          {tasks.length === 0 && (
            <div className="column-empty">
              <p>Sin tareas</p>
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

// Componente TaskCard
const TaskCard = ({
  task,
  onEdit,
  onDelete,
  onToggleTimer,
  onViewTimeLogs,
  onOpenPostDetail,
  getPrioridadColor,
  formatTime,
  localTimers,
  puedeEditar
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: !puedeEditar
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isVencida = task.fecha_entrega && new Date(task.fecha_entrega) < new Date();

  const handleCardClick = useCallback((e) => {
    // No expandir si se hizo click en un botón
    if (e.target.closest('button')) return;
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const handleExpandClick = useCallback((e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  if (isDragging) {
    return (
      <div className="kanban-card dragging">
        <div className="kanban-card-header">
          <h4>{task.nombre_tarea}</h4>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`kanban-card ${isExpanded ? 'expanded' : 'collapsed'}`}
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="kanban-card-header">
        <h4>{task.nombre_tarea}</h4>
        <div className="kanban-card-actions" onClick={(e) => e.stopPropagation()}>
          {isExpanded && puedeEditar && (
            <>
              <button
                className="btn-icon btn-edit-small"
                onClick={() => onEdit(task)}
                title="Editar tarea"
              >
                ✏️
              </button>
              <button
                className="btn-icon btn-delete-small"
                onClick={() => onDelete(task.id, task.nombre_tarea)}
                title="Eliminar"
              >
                🗑️
              </button>
            </>
          )}
          <button
            className="btn-icon btn-expand-small"
            onClick={handleExpandClick}
            title={isExpanded ? "Contraer" : "Expandir"}
          >
            {isExpanded ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {/* Vista contraída - Solo cliente, post asignado y usuario */}
      {!isExpanded && (
        <div className="kanban-card-collapsed-info">
          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
            {task.cliente_nombre && (
              <span
                className="cliente-badge-tiny"
                style={{ backgroundColor: task.cliente_color || '#6b7280' }}
              >
                {task.cliente_nombre}
              </span>
            )}
            {task.post_asignado_titulo && (
              <span
                className="post-badge-tiny"
                style={{
                  backgroundColor: '#69B32D',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
                onClick={(e) => onOpenPostDetail(task.post_asignado, e)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#7ac234';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#69B32D';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title={`Click para ver: ${task.post_asignado_titulo}`}
              >
                📄 {task.post_asignado_titulo}
              </span>
            )}
          </div>
          <div className="user-info-tiny">
            <div
              className="user-avatar-tiny"
              style={{ backgroundColor: getAvatarColor(task.asignado_a_nombre || 'Sin asignar') }}
              title={task.asignado_a_nombre || 'Sin asignar'}
            >
              {getInitials(task.asignado_a_nombre || 'Sin asignar')}
            </div>
            <span className="user-name-tiny">{task.asignado_a_nombre || 'Sin asignar'}</span>
          </div>
        </div>
      )}

      {/* Vista expandida - Todo el contenido */}
      {isExpanded && (
        <>
          {/* Cliente y Post badges */}
          {(task.cliente_nombre || task.post_asignado_titulo) && (
            <div className="kanban-card-cliente" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {task.cliente_nombre && (
                <span
                  className="cliente-badge-small"
                  style={{ backgroundColor: task.cliente_color || '#6b7280' }}
                >
                  {task.cliente_nombre}
                </span>
              )}
              {task.post_asignado_titulo && (
                <span
                  className="post-badge-small"
                  style={{
                    backgroundColor: '#69B32D',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}
                  onClick={(e) => onOpenPostDetail(task.post_asignado, e)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#7ac234';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#69B32D';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={`Click para ver: ${task.post_asignado_titulo}`}
                >
                  📄 {task.post_asignado_titulo}
                </span>
              )}
            </div>
          )}

          {/* Descripción corta */}
          {task.descripcion && (
            <p className="kanban-card-descripcion">{task.descripcion}</p>
          )}

          {/* Info */}
          <div className="kanban-card-info">
            {/* Avatar del usuario asignado */}
            <div className="info-item-small">
              <div
                className="user-avatar"
                style={{ backgroundColor: getAvatarColor(task.asignado_a_nombre || 'Sin asignar') }}
                title={task.asignado_a_nombre || 'Sin asignar'}
              >
                {getInitials(task.asignado_a_nombre || 'Sin asignar')}
              </div>
              <span>{task.asignado_a_nombre || 'Sin asignar'}</span>
            </div>

            {/* Fecha de entrega */}
            {task.fecha_entrega && (
              <div className={`info-item-small ${isVencida ? 'vencida' : ''}`}>
                📅
                <span>{new Date(task.fecha_entrega).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</span>
                {isVencida && '⚠️'}
              </div>
            )}
          </div>

          {/* Tags */}
          {task.tags_list && task.tags_list.length > 0 && (
            <div className="kanban-card-tags">
              {task.tags_list.slice(0, 3).map((tag, index) => (
                <span key={index} className="tag-small">{tag}</span>
              ))}
              {task.tags_list.length > 3 && (
                <span className="tag-small more">+{task.tags_list.length - 3}</span>
              )}
            </div>
          )}

          {/* Cronómetro */}
          <div className="kanban-card-timer" onClick={(e) => e.stopPropagation()}>
            <div className="timer-display-small">
              ⏱️
              <span className={`time-small ${task.is_timer_active ? 'active' : ''}`}>
                {formatTime(localTimers[task.id] || task.total_time_seconds || 0)}
              </span>
            </div>
            {puedeEditar && (
              <>
                {!task.is_timer_active ? (
                  <button
                    className="btn-timer-small btn-start-small"
                    onClick={() => onToggleTimer(task)}
                    title="Iniciar cronómetro"
                  >
                    ▶️
                  </button>
                ) : (
                  <button
                    className="btn-timer-small btn-stop-small"
                    onClick={() => onToggleTimer(task)}
                    title="Detener cronómetro"
                  >
                    ⏹️
                  </button>
                )}
              </>
            )}
            {isExpanded && (
              <button
                className="btn-history-small"
                onClick={() => onViewTimeLogs(task)}
                title="Ver historial"
              >
                🔄
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default KanbanTablero;